/* eslint-disable max-lines */
import { FullScreenIcon } from '@hakimo-ui/hakimo/ui-elements';
import { Button, Modal } from '@hakimo-ui/shared/ui-base';
import {
  CameraIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useState } from 'react';
import { LABEL } from './util';

interface PicturePickerProps {
  badge: React.ReactNode;
  previewList: string[];
  fileInputRef: React.RefObject<HTMLInputElement>;
  previewIdx: number | null;
  setPreviewIdx: (idx: number | null) => void;
  onFilesSelect: (files: File[]) => void;
  onFileRemove: (idx: number) => void;
  showHeader?: boolean;
  readOnly?: boolean;
  allowRemove?: boolean;
  wrap?: boolean;
  noIndent?: boolean;
  dashedEmptyTile?: boolean;
  large?: boolean;
}

const TILE_CLASS = 'h-20 w-36';
const TILE_CLASS_LARGE = 'h-28 w-48';

export function PicturePicker({
  badge,
  previewList,
  fileInputRef,
  previewIdx,
  setPreviewIdx,
  onFilesSelect,
  onFileRemove,
  showHeader = true,
  readOnly = false,
  allowRemove = false,
  wrap = false,
  noIndent = false,
  dashedEmptyTile = false,
  large = false,
}: PicturePickerProps) {
  const tileClass = large ? TILE_CLASS_LARGE : TILE_CLASS;
  const [isDragging, setIsDragging] = useState(false);
  const hasImages = previewList.length > 0;

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith('image/')
    );
    if (files.length) onFilesSelect(files);
  };

  return (
    <div className="flex flex-col gap-2.5">
      {showHeader && (
        <div className="flex items-center gap-2.5">
          {badge}
          <span className={LABEL}>Snapshots</span>
          {hasImages && (
            <span className="bg-onlight-bg-3 dark:bg-ondark-bg-3 text-onlight-text-1 dark:text-ondark-text-2 inline-flex items-center rounded-3xl px-3 py-0.5 text-xs font-medium">
              {previewList.length} Uploaded
            </span>
          )}
        </div>
      )}

      <div
        className={clsx('flex items-center gap-2', !noIndent && 'pl-6')}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragging(false);
        }}
        onDrop={handleDrop}
      >
        {!hasImages && !readOnly && (
          <div
            className={clsx(
              'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl transition-all',
              tileClass,
              isDragging
                ? 'ring-primary-600 bg-primary-50 dark:bg-primary-900/20 ring-2'
                : dashedEmptyTile
                ? 'border-onlight-line-3 dark:border-ondark-line-3 border'
                : 'bg-onlight-bg-3 dark:bg-ondark-bg-3'
            )}
            onClick={() => fileInputRef.current?.click()}
          >
            <CameraIcon className="text-onlight-text-3 dark:text-ondark-text-2 h-8 w-8 opacity-50" />
            <span className="text-onlight-text-3 dark:text-ondark-text-2 text-sm">
              {isDragging ? 'Drop here' : 'Upload'}
            </span>
          </div>
        )}

        {hasImages && (
          <div
            className={clsx(
              'flex items-center gap-2 rounded-lg pt-1 transition-all',
              wrap ? 'flex-wrap' : 'flex-1 overflow-x-auto',
              !readOnly && isDragging && 'ring-primary-600 ring-2'
            )}
          >
            {previewList.map((src, i) => (
              <div key={i} className="group relative shrink-0">
                <img
                  src={src}
                  alt={`preview-${i + 1}`}
                  className={clsx(
                    'cursor-pointer rounded-lg object-cover',
                    tileClass
                  )}
                />
                {(!readOnly || allowRemove) && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                    <Button
                      variant="error"
                      onClick={() => onFileRemove(i)}
                      className="!rounded-full !px-2"
                    >
                      <TrashIcon className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                <Button
                  variant="icon"
                  className="absolute right-1 top-1 bg-black/50 !p-0.5 hover:bg-black/75"
                  onClick={() => setPreviewIdx(i)}
                >
                  <FullScreenIcon className="fill-onlight-text-1 dark:fill-ondark-text-1 h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
        {hasImages && !readOnly && (
          <Button
            variant="icon"
            className={clsx(
              'ml-auto flex shrink-0 items-center',
              wrap ? 'mr-2 gap-1 !p-1' : 'flex-col gap-0.5'
            )}
            onClick={() => fileInputRef.current?.click()}
          >
            <PlusIcon className="h-5 w-5" />
            <span className="text-sm">Add</span>
          </Button>
        )}
      </div>

      {!readOnly && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            const f = Array.from(e.target.files ?? []);
            if (f.length) onFilesSelect(f);
            e.target.value = '';
          }}
        />
      )}

      <Modal
        open={previewIdx !== null}
        onClose={() => setPreviewIdx(null)}
        title={
          previewIdx !== null
            ? `Snapshot ${previewIdx + 1} of ${previewList.length}`
            : ''
        }
        closable
      >
        <div className="w-full max-w-4xl p-2">
          <div className="relative aspect-video overflow-hidden rounded-xl bg-black">
            {previewIdx !== null && (
              <img
                src={previewList[previewIdx]}
                alt={`Preview ${previewIdx + 1}`}
                className="h-full w-full object-contain"
              />
            )}
            {previewList.length > 1 && previewIdx !== null && (
              <Button
                variant="icon"
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 !p-1.5 hover:bg-black/75"
                onClick={() =>
                  setPreviewIdx(
                    (previewIdx - 1 + previewList.length) % previewList.length
                  )
                }
              >
                <ChevronLeftIcon className="h-5 w-5 text-white" />
              </Button>
            )}
            {previewList.length > 1 && previewIdx !== null && (
              <Button
                variant="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 !p-1.5 hover:bg-black/75"
                onClick={() =>
                  setPreviewIdx((previewIdx + 1) % previewList.length)
                }
              >
                <ChevronRightIcon className="h-5 w-5 text-white" />
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
